import React, { useState, useMemo, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { GitMerge, AlertTriangle, ArrowRight } from "lucide-react";

/**
 * Dimension Merge dialog (Iter-190) —
 * Fold one cost centre into another. Rewrites every historical voucher line,
 * budget, department and mirrored business segment so no dimensional history
 * is lost. Then deletes the source cost centre.
 */
export default function MergeCcDialog({ open, onOpenChange, ccs, initialSrc, onDone }) {
  const [src, setSrc] = useState("");
  const [tgt, setTgt] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (open) { setSrc(initialSrc || ""); setTgt(""); setPreview(null); }
  }, [open, initialSrc]);

  const options = useMemo(() => (ccs || []).map((c) => ({
    id: c.id, label: `${c.code || "—"} · ${c.name}`, name: c.name,
  })), [ccs]);
  const srcCc = options.find((o) => o.id === src);
  const tgtCc = options.find((o) => o.id === tgt);

  const doMerge = async () => {
    if (!src || !tgt || src === tgt) {
      toast.error("Pick two different dimensions");
      return;
    }
    if (!window.confirm(
      `Fold "${srcCc?.name}" into "${tgtCc?.name}"?\n\n` +
      `• Every historical voucher line, budget and department tagged with "${srcCc?.name}" will be re-tagged to "${tgtCc?.name}".\n` +
      `• The source dimension "${srcCc?.name}" will be deleted.\n` +
      `• This action cannot be undone.`
    )) return;
    setBusy(true);
    try {
      const r = await api.post(`/cost-centres/${src}/merge-into/${tgt}`);   // legacy merge endpoint (kept)
      const s = r.data?.stats || {};
      setPreview({ ...s, source: r.data?.source, target: r.data?.target });
      toast.success(
        `Merged "${r.data?.source}" → "${r.data?.target}" · ` +
        `${s.voucher_lines_updated ?? 0} lines · ${s.budgets_updated ?? 0} budgets · ` +
        `${s.departments_updated ?? 0} departments`
      );
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="cc-merge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="w-4 h-4 text-orange-600" />
            Merge dimensions
          </DialogTitle>
          <DialogDescription>
            When an import created a duplicate (e.g. "Blore" + "Bengaluru"), fold one into the other.
            Historical vouchers are re-tagged — no dimensional data is lost.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs">Source (will be deleted)</Label>
            <Select value={src} onValueChange={setSrc}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="cc-merge-src">
                <SelectValue placeholder="Pick the dimension to fold in" />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.id} value={o.id} disabled={o.id === tgt}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-center text-muted-foreground">
            <ArrowRight className="w-4 h-4" />
          </div>
          <div>
            <Label className="text-xs">Target (will absorb everything)</Label>
            <Select value={tgt} onValueChange={setTgt}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="cc-merge-tgt">
                <SelectValue placeholder="Pick the surviving dimension" />
              </SelectTrigger>
              <SelectContent>
                {options.map((o) => (
                  <SelectItem key={o.id} value={o.id} disabled={o.id === src}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {src && tgt && src === tgt && (
            <div className="text-xs text-red-600 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Pick two different dimensions.
            </div>
          )}

          {preview && (
            <div className="rounded-sm border border-emerald-300 bg-emerald-50 p-3 text-xs space-y-1"
                  data-testid="cc-merge-preview">
              <div className="font-mono uppercase tracking-widest text-[10px] text-emerald-700">Merge report</div>
              <div><b>{preview.source}</b> → <b>{preview.target}</b></div>
              <div>Voucher lines re-tagged: <span className="font-mono">{preview.voucher_lines_updated ?? 0}</span></div>
              <div>Voucher headers re-tagged: <span className="font-mono">{preview.vouchers_updated ?? 0}</span></div>
              <div>Departments updated: <span className="font-mono">{preview.departments_updated ?? 0}</span></div>
              <div>Budgets updated: <span className="font-mono">{preview.budgets_updated ?? 0}</span></div>
              <div>Children re-parented: <span className="font-mono">{preview.children_reparented ?? 0}</span></div>
              {preview.segments_merged ? <div>Business segment mirror also merged.</div> : null}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={doMerge}
                  disabled={busy || !src || !tgt || src === tgt}
                  className="rounded-sm bg-gradient-to-r from-orange-600 to-red-600 text-white gap-1.5"
                  data-testid="cc-merge-submit">
            <GitMerge className="w-3.5 h-3.5" /> {busy ? "Merging…" : "Merge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
